/**
 * Date Utility Functions
 * All dates formatted as dd/mm/yyyy (Indian format)
 */

/**
 * Format date to dd/mm/yyyy
 * @param {string|Date} dateString - Date string or Date object
 * @returns {string} Formatted date as dd/mm/yyyy or 'N/A' if invalid
 */
export const formatDate = (dateString) => {
  if (!dateString) return 'N/A';

  try {
    const date = new Date(dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) return 'N/A';

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  } catch (error) {
    return 'N/A';
  }
};

/**
 * Format date to dd/mm/yyyy with full month name
 * @param {string|Date} dateString - Date string or Date object
 * @returns {string} Formatted date as "DD Month YYYY" or 'N/A' if invalid
 */
export const formatDateLong = (dateString) => {
  if (!dateString) return 'N/A';

  try {
    const date = new Date(dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) return 'N/A';

    const day = date.getDate();
    const month = date.toLocaleDateString('en-IN', { month: 'long' });
    const year = date.getFullYear();

    return `${day} ${month} ${year}`;
  } catch (error) {
    return 'N/A';
  }
};

/**
 * Format date to dd/mm/yyyy HH:MM (with time)
 * @param {string|Date} dateString - Date string or Date object
 * @returns {string} Formatted date-time as dd/mm/yyyy HH:MM
 */
export const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';

  try {
    const date = new Date(dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) return 'N/A';

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${day}/${month}/${year} ${hours}:${minutes}`;
  } catch (error) {
    return 'N/A';
  }
};

/**
 * Format date for input field (yyyy-mm-dd)
 * Date inputs require yyyy-mm-dd format
 * @param {string|Date} dateString - Date string or Date object
 * @returns {string} Formatted date as yyyy-mm-dd
 */
export const formatDateForInput = (dateString) => {
  if (!dateString) return '';

  try {
    const date = new Date(dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) return '';

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  } catch (error) {
    return '';
  }
};

/**
 * Calculate age from date of birth
 * @param {string|Date} dob - Date of birth
 * @returns {number} Age in years or 0 if invalid
 */
export const calculateAge = (dob) => {
  if (!dob) return 0;

  try {
    const today = new Date();
    const birthDate = new Date(dob);

    if (isNaN(birthDate.getTime())) return 0;

    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age;
  } catch (error) {
    return 0;
  }
};

/**
 * Format date range
 * @param {string|Date} startDate - Start date
 * @param {string|Date} endDate - End date
 * @returns {string} Formatted date range as "DD/MM/YYYY - DD/MM/YYYY"
 */
export const formatDateRange = (startDate, endDate) => {
  const start = formatDate(startDate);
  const end = formatDate(endDate);

  if (start === 'N/A' && end === 'N/A') return 'N/A';
  if (start === 'N/A') return `Until ${end}`;
  if (end === 'N/A') return `From ${start}`;

  return `${start} - ${end}`;
};

/**
 * Get relative time (e.g., "2 days ago", "in 3 days")
 * @param {string|Date} dateString - Date string or Date object
 * @returns {string} Relative time string
 */
export const getRelativeTime = (dateString) => {
  if (!dateString) return 'N/A';

  try {
    const date = new Date(dateString);
    const now = new Date();

    if (isNaN(date.getTime())) return 'N/A';

    const diffMs = date - now;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays === -1) return 'Yesterday';
    if (diffDays > 0) return `In ${diffDays} days`;
    if (diffDays < 0) return `${Math.abs(diffDays)} days ago`;

    return formatDate(dateString);
  } catch (error) {
    return 'N/A';
  }
};

/**
 * Check if date is in the past
 * @param {string|Date} dateString - Date string or Date object
 * @returns {boolean} True if date is in the past
 */
export const isPastDate = (dateString) => {
  if (!dateString) return false;

  try {
    const date = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return date < today;
  } catch (error) {
    return false;
  }
};

/**
 * Check if date is today
 * @param {string|Date} dateString - Date string or Date object
 * @returns {boolean} True if date is today
 */
export const isToday = (dateString) => {
  if (!dateString) return false;

  try {
    const date = new Date(dateString);
    const today = new Date();

    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  } catch (error) {
    return false;
  }
};

// Export all functions
export default {
  formatDate,
  formatDateLong,
  formatDateTime,
  formatDateForInput,
  calculateAge,
  formatDateRange,
  getRelativeTime,
  isPastDate,
  isToday,
};
